import React, { useRef, useState, useCallback } from 'react';
import { Button, Snackbar, Alert } from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { useAppSelector } from '../../../../../app/store';
import { useUploadDrgsMutation } from '../../../../../services/ContractService';
import { useSignalR } from '../../../../../hooks/useSignalR';
import { UploadProgress } from '../../../../../services/signalRService';

interface DrgUploadCsvProps {
  disabled?: boolean;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
  onProgressUpdate?: (progress: UploadProgress) => void;
  onComplete?: (progress: UploadProgress) => void;
  onError?: (progress: UploadProgress) => void;
}

const DrgUploadCsv: React.FC<DrgUploadCsvProps> = ({
  disabled,
  onUploadStart,
  onUploadComplete,
  onProgressUpdate,
  onComplete,
  onError
}) => {
  const selectedFeeSchedule = useAppSelector((state) => state.contract.currentFeeSchedule);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadDrgs, { isLoading: isUploading, error: uploadError }] = useUploadDrgsMutation();

  // SignalR callbacks
  const onProgress = useCallback((progress: UploadProgress) => {
    console.log('DRG Upload Progress:', progress);
    onProgressUpdate?.(progress);
  }, [onProgressUpdate]);

  const onCompleted = useCallback((progress: UploadProgress) => {
    console.log('DRG Upload Completed:', progress);
    onProgressUpdate?.(progress);
    onComplete?.(progress);
    setIsUploadInProgress(false);
    onUploadComplete?.();
  }, [onProgressUpdate, onComplete, onUploadComplete]);

  const onErrorCallback = useCallback((progress: UploadProgress) => {
    console.log('DRG Upload Error:', progress);
    onProgressUpdate?.(progress);
    onError?.(progress);
    setIsUploadInProgress(false);
    onUploadComplete?.();
  }, [onProgressUpdate, onError, onUploadComplete]);

  // Initialize SignalR connection for Contracts service
  const { isConnected: isSignalRConnected } = useSignalR({
    serviceName: 'Contracts',
    onProgress,
    onCompleted,
    onError: onErrorCallback,
    autoConnect: true
  });

  const handleUploadClick = () => {
    if (!selectedFeeSchedule?.id) {
      console.warn('No fee schedule selected');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && selectedFeeSchedule?.id) {
      try {
        // Reset progress state and start tracking
        setIsUploadInProgress(true);
        setErrorMessage(null);
        onUploadStart?.();

        const result = await uploadDrgs({
          feeScheduleId: selectedFeeSchedule.id,
          file: file
        }).unwrap();
        
        // Show upload results
        const message = `Upload completed: ${result.successful} successful, ${result.failed} failed`;
        console.log(message, result);

        // If SignalR didn't handle completion, manually stop progress tracking
        if (isUploadInProgress) {
          setIsUploadInProgress(false);
          onUploadComplete?.();
        }
      } catch (err: any) {
        console.error('Upload failed:', err);
        const message = err?.data?.message || err?.message || 'Upload failed. Please try again.';
        setErrorMessage(message);
        setIsUploadInProgress(false);
        onUploadComplete?.();
      }
    }
    // Clear the input so the same file can be uploaded again (regardless of success or failure)
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isDisabled = disabled || !selectedFeeSchedule?.id || isUploading || isUploadInProgress;

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={handleUploadClick}
        size="small"
        disabled={isDisabled}
      >
        {isUploadInProgress ? 'Uploading...' : 'Upload CSV'}
      </Button>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv"
        style={{ display: 'none' }}
      />

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DrgUploadCsv;
